import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './tariff-history.reducer';

export const TariffHistory = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const tariffHistoryList = useAppSelector(state => state.tariffHistory.entities);
  const loading = useAppSelector(state => state.tariffHistory.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="tariff-history-heading" data-cy="TariffHistoryHeading">
        <Translate contentKey="crawlerBackendApp.tariffHistory.home.title">Tariff Histories</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.tariffHistory.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/tariff-history/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.tariffHistory.home.createLabel">
              Create new Tariff History
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {tariffHistoryList && tariffHistoryList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('tariff')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.tariff">Tariff</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('tariff')} />
                </th>
                <th className="hand" onClick={sort('effectiveDate')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.effectiveDate">Effective Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('effectiveDate')} />
                </th>
                <th className="hand" onClick={sort('expirationDate')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.expirationDate">Expiration Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('expirationDate')} />
                </th>
                <th className="hand" onClick={sort('supplierId')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.supplierId">Supplier Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('supplierId')} />
                </th>
                <th className="hand" onClick={sort('contractType')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.contractType">Contract Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('contractType')} />
                </th>
                <th className="hand" onClick={sort('variant')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.variant">Variant</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('variant')} />
                </th>
                <th className="hand" onClick={sort('commercialBusiness')}>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.commercialBusiness">
                    Commercial Business
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('commercialBusiness')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.tariffHistory.tariffType">Tariff Type</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tariffHistoryList.map((tariffHistory, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/tariff-history/${tariffHistory.id}`} color="link" size="sm">
                      {tariffHistory.id}
                    </Button>
                  </td>
                  <td>{tariffHistory.tariff}</td>
                  <td>
                    {tariffHistory.effectiveDate ? (
                      <TextFormat type="date" value={tariffHistory.effectiveDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {tariffHistory.expirationDate ? (
                      <TextFormat type="date" value={tariffHistory.expirationDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{tariffHistory.supplierId}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.ContractType.${tariffHistory.contractType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.Variant.${tariffHistory.variant}`} />
                  </td>
                  <td>
                    <Translate
                      contentKey={`crawlerBackendApp.CommercialBusiness.${tariffHistory.commercialBusiness}`}
                    />
                  </td>
                  <td>
                    {tariffHistory.tariffType ? (
                      <Link to={`/tariff/${tariffHistory.tariffType.id}`}>{tariffHistory.tariffType.type}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/tariff-history/${tariffHistory.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/tariff-history/${tariffHistory.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/tariff-history/${tariffHistory.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.tariffHistory.home.notFound">
                No Tariff Histories found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default TariffHistory;
